/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { format, parseISO } from "date-fns";
import { CustomerDetails, WrapTextCellProps } from "../../types";
import { usePagePermissions } from "../../Components/PagePermission";
import { BASE_API_URL } from "../../API/Base";
import AxiosInstance from "../../API/axios";
import { Delete, EditNote, MenuBook } from "@mui/icons-material";
import toast from "react-hot-toast";
import Layout from "../../Components/Layout";
import AddItemButton from "../../Components/AddItemButton";
import DeleteModal from "../../Modals/DeleteModal";

export const formatDate = (dateString: string) => {
  if (!dateString) {
    return "N/A";
  }
  const date = parseISO(dateString);
  return format(date, "M/dd/yyyy h:mm:ss a");
};

const Status = () => {
  const [rows, setRows] = useState<CustomerDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowCount, setRowCount] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const navigate = useNavigate();

  // Fetch permissions for Address module
  const permissions = usePagePermissions(20);
  // const theme: Theme = useThemeStore((state) => state.theme);

  const base64Encode = (str: string) => {
    return btoa(str);
  };
  const base64Decode = (encodedStr: string) => {
    return atob(encodedStr);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  const fetchCustomerData = async (
    page: number,
    pageSize: number,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `${BASE_API_URL}/admin/status/v1/status`,
        {
          params: {
            page,
            per_page: pageSize,
            search_query: searchValue,
          },
        }
      );

      if (
        !response.data ||
        !response.data.status_data ||
        response.data.total_count == null
      ) {
        console.error("Invalid response format:", response.data);
        setLoading(false);
        return;
      }

      const formattedData = response.data.status_data.map(
        (row: CustomerDetails, index: number) => ({
          ...row,
          actionsID: startIndex + index + 1,
          Id: row.Id,
        })
      );

      setRows(formattedData);
      setRowCount(response.data.total_count);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.List) {
      fetchCustomerData(currentPage, pageSize, debouncedSearchValue);
    }
  }, [currentPage, pageSize, permissions, debouncedSearchValue]);

  // Calculate total pages
  const totalPages = Math.ceil(rowCount / pageSize);

  // Calculate start and end index for the visible rows
  const startIndex = (currentPage - 1) * pageSize;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Add logic to fetch data for the new page or perform any other actions
  };
  const WrapTextCell: React.FC<WrapTextCellProps> = ({ value }) => {
    return <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>;
  };

  const handleCreate = () => {
    navigate("/status/manage/form/create");
  };

  const handleEdit = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/status/manage/form/edit/${encodedId}`);
  };

  const handleView = (row: any) => {
    const encodedId = base64Encode(row.Id);
    navigate(`/status/manage/form/view/${encodedId}`);
  };

  const handleDelete = (id: string) => {
    const encodedId = base64Encode(id);
    setIsDeleteModalOpen(true);
    setSelectedRowId(encodedId);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  // Conditionally define the Action column based on permissions
  const actionColumn =
    permissions?.Update || permissions?.Delete || permissions?.View
      ? {
          field: "action",
          headerName: "Action",
          width: 120,
          renderCell: (params: any) => (
            <div className="flex flex-row gap-x-1">
              {permissions?.Update && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Edit cursor-pointer"
                  onClick={() => {
                    handleEdit(params.row);
                  }}
                >
                  <Tooltip title="Edit">
                    <EditNote sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.Delete && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Delete cursor-pointer"
                  onClick={() => {
                    handleDelete(params.row.Id);
                    const actionId = permissions?.Delete?.id;
                  }}
                >
                  <Tooltip title="Delete">
                    <Delete sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.View && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-View cursor-pointer flex flex-row items-center justify-center"
                  onClick={() => {
                    handleView(params.row);
                    const actionId = permissions?.View?.id;
                  }}
                >
                  <Tooltip title="View">
                    <MenuBook sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
            </div>
          ),
        }
      : undefined;

  const columns: GridColDef[] = [
    {
      field: "actionsID",
      headerName: "ID",
      width: 70,
      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
    },
    ...(actionColumn ? [actionColumn] : []),
    {
      field: "Status_Name",
      headerName: "Status",
      cellClassName: "capitalize",
      width: 250,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },

    {
      field: "Status_Type",
      headerName: "Status type",
      cellClassName: "capitalize",
      width: 200,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },

    {
      field: "IsStatus",
      headerName: "Status",
      cellClassName: "capitalize",
      width: 150,
      renderCell: (params) => (
        <div
          className={`cellWithStatus ${params.value ? "active" : "inactive"}`}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "Parent_Status_Id",
      headerName: "Parent Status Id",
      width: 150,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    try {
      const decodedId = selectedRowId ? base64Decode(selectedRowId) : null;
      const response = await AxiosInstance.delete(
        `${BASE_API_URL}/admin/status/v1/status/${decodedId}`
      );

      if (response.data) {
        // Remove the deleted row from the local state
        setIsDeleteModalOpen(false);
        setRows((prevRows) =>
          prevRows.filter((row: any) => row.Id.toString() !== decodedId)
        );
        toast.success("Data deleted successfully");
      } else {
        toast.error("Error deleting data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Layout PageId={5}>
      <div className="flex flex-col items-center justify-start w-full h-full">
        <div
          id="actions"
          className="flex flex-col md:flex-row items-center justify-between w-full gap-x-2"
        >
          {/* <div className="flex flex-row items-center justify-center md:w-1/4 w-full ">
            <SearchInput
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search"
            />
          </div> */}
          <div className="flex md:justify-end md:space-x-2 justify-between items-center w-full ">
            {permissions?.Add && (
              <AddItemButton
                label="Add new"
                size="small"
                onClick={handleCreate}
              />
            )}
            {/* <ActionButtonsWithDropdown /> */}
          </div>
        </div>

        <div className="flex flex-col items-start justify-start w-full mt-2">
          <div className="w-full">
            <Box>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.Id}
                autoHeight
                loading={loading}
              />
            </Box>
          </div>
        </div>
        {/* <PaginationButtons
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        /> */}
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        message="Are you sure you want to delete this data?"
      />
    </Layout>
  );
};

export default Status;
